import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
	ReactFlow,
	MiniMap,
	Controls,
	Background,
	useNodesState,
	useEdgesState,
	addEdge,
	Handle,
	Position,
	MarkerType,
	BaseEdge,
	EdgeLabelRenderer,
	getBezierPath,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import PropTypes from "prop-types";
import dagre from "dagre";

// Beautiful animated edge with smooth label positioning
const AnimatedLabelEdge = ({ id, sourceX, sourceY, targetX, targetY, markerEnd, style, data }) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
  });

  return (
    <>
      <BaseEdge 
        id={id} 
        path={edgePath} 
        markerEnd={markerEnd} 
        style={{
          ...style,
          strokeDasharray: data?.animated ? "8 4" : "none",
        }} 
      />
      {data?.label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(226, 232, 240, 0.8)",
              color: data.color || "#475569",
              padding: "6px 12px",
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
              pointerEvents: "all",
              transition: "all 0.2s ease",
              maxWidth: 120,
              textAlign: "center",
              zIndex: 1000,
            }}
          >
            {data.label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

AnimatedLabelEdge.propTypes = {
  id: PropTypes.string.isRequired,
  sourceX: PropTypes.number.isRequired,
  sourceY: PropTypes.number.isRequired,
  targetX: PropTypes.number.isRequired,
  targetY: PropTypes.number.isRequired,
  markerEnd: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  style: PropTypes.object,
  data: PropTypes.shape({
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    color: PropTypes.string,
    animated: PropTypes.bool,
    tier: PropTypes.oneOf(["parent", "decision", "actor", "receiver"]),
  }),
};// Modern Decision Node with professional styling and borders
const DecisionNode = ({ data, selected }) => {
	const { title, unit, isCurrentDecision, hasChildren, isExpanded, onToggleExpand, decisionId } = data;
	const [isHovered, setIsHovered] = useState(false);
	
	const handleToggle = (e) => {
		e.stopPropagation();
		if (hasChildren && onToggleExpand) {
			onToggleExpand(decisionId);
		}
	};

	const nodeStyle = {
		padding: "18px 22px",
		borderRadius: 12,
		background: isCurrentDecision 
			? "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #bae6fd 100%)"
			: "linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%)",
		border: isCurrentDecision 
			? `3px solid ${isHovered ? "#0284c7" : "#0ea5e9"}`
			: `2px solid ${isHovered ? "#64748b" : "#cbd5e1"}`,
		boxShadow: isCurrentDecision 
			? isHovered 
				? "0 20px 40px rgba(14, 165, 233, 0.25), 0 8px 16px rgba(14, 165, 233, 0.15)"
				: "0 12px 24px rgba(14, 165, 233, 0.2), 0 4px 12px rgba(14, 165, 233, 0.1)"
			: isHovered 
				? "0 12px 24px rgba(15, 23, 42, 0.12), 0 4px 12px rgba(15, 23, 42, 0.08)"
				: "0 6px 16px rgba(15, 23, 42, 0.08), 0 2px 6px rgba(15, 23, 42, 0.04)",
		minWidth: 280,
		maxWidth: 320,
		position: "relative",
		cursor: hasChildren ? "pointer" : "default",
		transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
		transform: isHovered ? "translateY(-2px) scale(1.02)" : "translateY(0) scale(1)",
	};

	const expandButtonStyle = {
		position: "absolute",
		top: 14,
		right: 14,
		width: 28,
		height: 28,
		borderRadius: "50%",
		background: isCurrentDecision 
			? "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
			: "linear-gradient(135deg, #64748b 0%, #475569 100%)",
		color: "white",
		border: "2px solid white",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		fontSize: 16,
		fontWeight: "700",
		cursor: "pointer",
		transition: "all 0.2s ease",
		transform: isExpanded ? "rotate(45deg)" : "rotate(0deg)",
		opacity: hasChildren ? 1 : 0,
		pointerEvents: hasChildren ? "all" : "none",
		boxShadow: "0 2px 8px rgba(15, 23, 42, 0.15)",
	};
	
	return (
		<div 
			style={nodeStyle}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<Handle 
				type="target" 
				position={Position.Top} 
				style={{ 
					background: isCurrentDecision ? "#0ea5e9" : "#64748b", 
					width: 12, 
					height: 12, 
					border: "3px solid white",
					boxShadow: "0 2px 8px rgba(15, 23, 42, 0.15)",
				}} 
			/>
			
			{hasChildren && (
				<button style={expandButtonStyle} onClick={handleToggle}>
					{isExpanded ? "−" : "+"}
				</button>
			)}
			
			<div style={{
				fontSize: 11,
				fontWeight: "800",
				color: isCurrentDecision ? "#0369a1" : "#475569",
				textTransform: "uppercase",
				letterSpacing: "1.2px",
				marginBottom: 10,
			}}>
				{isCurrentDecision ? "Current Decision" : "Sub Decision"}
			</div>
			
			<div style={{
				fontSize: 17,
				fontWeight: "700",
				color: "#0f172a",
				marginBottom: 10,
				lineHeight: "1.4",
				wordBreak: "break-word",
			}}>
				{title}
			</div>
			
			<div style={{
				fontSize: 13,
				color: "#64748b",
				fontWeight: "600",
			}}>
				<strong>Unit:</strong> {unit}
			</div>
			
			<Handle 
				type="source" 
				position={Position.Bottom} 
				style={{ 
					background: isCurrentDecision ? "#0ea5e9" : "#64748b", 
					width: 12, 
					height: 12, 
					border: "3px solid white",
					boxShadow: "0 2px 8px rgba(15, 23, 42, 0.15)",
				}} 
			/>
		</div>
	);
};

// Actor Node with purple theme and modern borders
const ActorNode = ({ data, selected }) => {
	const [isHovered, setIsHovered] = useState(false);

	const nodeStyle = {
		padding: "14px 18px",
		borderRadius: 10,
		background: "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 50%, #e9d5ff 100%)",
		border: `2px solid ${isHovered ? "#8b5cf6" : "#c4b5fd"}`,
		boxShadow: isHovered 
			? "0 12px 24px rgba(139, 92, 246, 0.2), 0 4px 12px rgba(139, 92, 246, 0.1)"
			: "0 6px 16px rgba(139, 92, 246, 0.12), 0 2px 6px rgba(139, 92, 246, 0.06)",
		minWidth: 200,
		maxWidth: 240,
		position: "relative",
		transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
		transform: isHovered ? "translateY(-2px) scale(1.02)" : "translateY(0) scale(1)",
	};

	return (
		<div 
			style={nodeStyle}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<Handle 
				type="target" 
				position={Position.Top} 
				style={{ 
					background: "#8b5cf6", 
					width: 10, 
					height: 10, 
					border: "3px solid white",
					boxShadow: "0 2px 8px rgba(139, 92, 246, 0.2)",
				}} 
			/>
			
			<div style={{
				fontSize: 10,
				fontWeight: "800",
				color: "#7c3aed",
				marginBottom: 8,
				textTransform: "uppercase",
				letterSpacing: "1.2px",
			}}>
				Actor
			</div>
			
			<div style={{
				fontSize: 15,
				fontWeight: "700",
				color: "#581c87",
				marginBottom: 6,
				lineHeight: "1.3",
			}}>
				{data.name}
			</div>
			
			<div style={{
				fontSize: 12,
				color: "#7c3aed",
				fontWeight: "600",
			}}>
				<strong>Role:</strong> {data.role}
			</div>
			
			<Handle 
				type="source" 
				position={Position.Bottom} 
				style={{ 
					background: "#8b5cf6", 
					width: 10, 
					height: 10, 
					border: "3px solid white",
					boxShadow: "0 2px 8px rgba(139, 92, 246, 0.2)",
				}} 
			/>
		</div>
	);
};

// Receiver Node with emerald theme and modern styling
const ReceiverNode = ({ data, selected }) => {
	const [isHovered, setIsHovered] = useState(false);

	const nodeStyle = {
		padding: "14px 18px",
		borderRadius: 10,
		background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 50%, #a7f3d0 100%)",
		border: `2px solid ${isHovered ? "#10b981" : "#6ee7b7"}`,
		boxShadow: isHovered 
			? "0 12px 24px rgba(16, 185, 129, 0.2), 0 4px 12px rgba(16, 185, 129, 0.1)"
			: "0 6px 16px rgba(16, 185, 129, 0.12), 0 2px 6px rgba(16, 185, 129, 0.06)",
		minWidth: 200,
		maxWidth: 240,
		position: "relative",
		transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
		transform: isHovered ? "translateY(-2px) scale(1.02)" : "translateY(0) scale(1)",
	};

	return (
		<div 
			style={nodeStyle}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<Handle 
				type="target" 
				position={Position.Top} 
				style={{ 
					background: "#10b981", 
					width: 10, 
					height: 10, 
					border: "3px solid white",
					boxShadow: "0 2px 8px rgba(16, 185, 129, 0.2)",
				}} 
			/>
			
			<div style={{
				fontSize: 10,
				fontWeight: "800",
				color: "#059669",
				marginBottom: 8,
				textTransform: "uppercase",
				letterSpacing: "1.2px",
			}}>
				Receiver
			</div>
			
			<div style={{
				fontSize: 15,
				fontWeight: "700",
				color: "#047857",
				marginBottom: 6,
				lineHeight: "1.3",
			}}>
				{data.receiverName}
			</div>
		</div>
	);
};

// Node types
const nodeTypes = {
	decision: DecisionNode,
	actor: ActorNode,
	receiver: ReceiverNode,
};

const edgeTypes = {
	animatedLabel: AnimatedLabelEdge,
};

const DecisionTree = ({ decisionData, directionsData = [], allDecisions = [], incomingRelations = [], decisionReceivers = [] }) => {
	const [nodesState, setNodes, onNodesChange] = useNodesState([]);
	const [edgesState, setEdges, onEdgesChange] = useEdgesState([]);
	const [rfInstance, setRfInstance] = useState(null);

	// State for managing expanded/collapsed decisions
	const [expandedDecisions, setExpandedDecisions] = useState(new Set([decisionData.id]));
	
	// Toggle expand/collapse with improved stability
	const toggleDecisionExpansion = useCallback((decisionId) => {
		setExpandedDecisions(prev => {
			const newSet = new Set(prev);
			if (newSet.has(decisionId)) {
				newSet.delete(decisionId);
			} else {
				newSet.add(decisionId);
			}
			return newSet;
		});
	}, []);
	
	// Stable layout with clear tier separation to prevent any overlaps
	const layoutGraph = useCallback((nodes, edges) => {
		const layoutedNodes = [];
		const nodeMap = new Map();
		
		// Categorize nodes by type
		const decisionNodes = nodes.filter(n => n.type === "decision");
		const actorNodes = nodes.filter(n => n.type === "actor");
		const receiverNodes = nodes.filter(n => n.type === "receiver");
		
		// Much larger layout parameters with clear tier separation
		const VERTICAL_SPACING = 500; // Increased even more for clear separation
		const HORIZONTAL_SPACING = 900; // Wider horizontal spacing
		const NODE_WIDTH = 280; // Even more conservative
		const NODE_HEIGHT = 180; // Even more conservative
		const START_X = 1000; // Move further right
		const START_Y = 200;
		
		// TIER 0: Root decision at top
		const rootDecision = decisionNodes.find(n => n.data?.isRoot || n.data?.isCurrentDecision);
		if (rootDecision) {
			const rootPosition = { x: START_X, y: START_Y };
			layoutedNodes.push({ ...rootDecision, position: rootPosition });
			nodeMap.set(rootDecision.id, rootPosition);
		}
		
		// TIER 1: Sub-decisions spread horizontally below root
		const subDecisions = decisionNodes.filter(n => !(n.data?.isRoot || n.data?.isCurrentDecision));
		subDecisions.forEach((node, index) => {
			const totalSubs = subDecisions.length;
			const minSpacing = Math.max(HORIZONTAL_SPACING, NODE_WIDTH + 250);
			const totalWidth = (totalSubs - 1) * minSpacing;
			const startX = START_X - (totalWidth / 2);
			
			const position = { 
				x: startX + (index * minSpacing), 
				y: START_Y + VERTICAL_SPACING // Clear separation from root
			};
			
			layoutedNodes.push({ ...node, position });
			nodeMap.set(node.id, position);
		});
		
		// TIER 2: Actors positioned well below ALL decisions
		// First, find the lowest decision Y position
		const allDecisionYPositions = layoutedNodes
			.filter(n => n.type === "decision")
			.map(n => n.position.y);
		const lowestDecisionY = Math.max(...allDecisionYPositions);
		
		actorNodes.forEach((node, index) => {
			const parentEdge = edges.find(e => e.target === node.id && e.data?.tier === "actor");
			const parentPos = parentEdge ? nodeMap.get(parentEdge.source) : { x: START_X, y: lowestDecisionY };
			
			// Group actors by their parent
			const actorsForParent = actorNodes.filter(an => {
				const edge = edges.find(e => e.target === an.id && e.data?.tier === "actor");
				return edge && edge.source === parentEdge?.source;
			});
			
			const actorIndex = actorsForParent.indexOf(node);
			const totalActors = actorsForParent.length;
			const minActorSpacing = Math.max(600, NODE_WIDTH + 200);
			const totalActorWidth = (totalActors - 1) * minActorSpacing;
			const startActorX = parentPos.x - (totalActorWidth / 2);
			
			const position = {
				x: totalActors === 1 ? parentPos.x : startActorX + (actorIndex * minActorSpacing),
				y: lowestDecisionY + VERTICAL_SPACING // Always below ALL decisions
			};
			
			layoutedNodes.push({ ...node, position });
			nodeMap.set(node.id, position);
		});
		
		// TIER 3: Receivers at the bottom
		receiverNodes.forEach((node, index) => {
			const parentEdge = edges.find(e => e.target === node.id);
			const parentPos = parentEdge ? nodeMap.get(parentEdge.source) : { x: START_X, y: START_Y + VERTICAL_SPACING * 2 };
			
			// Group receivers by their parent
			const receiversForParent = receiverNodes.filter(rn => {
				const edge = edges.find(e => e.target === rn.id);
				return edge && edge.source === parentEdge?.source;
			});
			
			const receiverIndex = receiversForParent.indexOf(node);
			const totalReceivers = receiversForParent.length;
			const minReceiverSpacing = Math.max(500, NODE_WIDTH + 150);
			const totalReceiverWidth = (totalReceivers - 1) * minReceiverSpacing;
			const startReceiverX = parentPos.x - (totalReceiverWidth / 2);
			
			const position = {
				x: totalReceivers === 1 ? parentPos.x : startReceiverX + (receiverIndex * minReceiverSpacing),
				y: parentPos.y + VERTICAL_SPACING // Below actors
			};
			
			layoutedNodes.push({ ...node, position });
			nodeMap.set(node.id, position);
		});
		
		return { nodes: layoutedNodes, edges };
	}, []);

	// Enhanced tree builder with hierarchical layout and collapsible nodes
	const buildDecisionTree = useMemo(() => {
		const nodes = [];
		const edges = [];
		const nodeIdSet = new Set();
		const edgeIdSet = new Set();
		
		const addNodeUnique = (n) => {
			if (!nodeIdSet.has(n.id)) {
				// Positions are computed by dagre
				n.position = n.position || { x: 0, y: 0 };
				nodes.push(n);
				nodeIdSet.add(n.id);
				return true;
			}
			return false;
		};
		
		const addEdgeUnique = (e) => { 
			if (!edgeIdSet.has(e.id)) { 
				edges.push(e); 
				edgeIdSet.add(e.id);
				return true;
			}
			return false;
		};



		const maxNodesPerRow = 3;
		
		// Check if root has children
		const rootChildren = directionsData?.filter(d => d.followDecision) || [];
		const rootHasChildren = rootChildren.length > 0;
		
		addNodeUnique({
			id: `decision-${decisionData.id}`,
			type: "decision",
			position: { x: 0, y: 0 },
			data: { 
				title: decisionData.name, 
				unit: decisionData.unit?.name || "N/A", 
				decisionId: decisionData.id, 
				isCurrentDecision: true,
				hasChildren: rootHasChildren,
				isExpanded: expandedDecisions.has(decisionData.id),
				onToggleExpand: toggleDecisionExpansion
			}
		});

		// Process decisions level by level for better hierarchy
		const processDecisionLevel = (decision, directions, level, parentX, isExpanded, startIndex = 0) => {
			if (!isExpanded) return startIndex; // Don't show children if collapsed
			
			// Separate node types
			const childDecisions = [];
			const actors = [];
			const receivers = level === 1 ? decisionReceivers : []; // Only root gets receivers
			
			if (Array.isArray(directions)) {
				directions.forEach((direction, idx) => {
					// Follow decisions
					if (direction.followDecision) {
						const fd = direction.followDecision;
						const childId = typeof fd === "object" ? fd.id : fd;
						const childDecision = allDecisions.find(d => d.id === childId) || (typeof fd === "object" ? fd : null);
						
						if (childDecision) {
							childDecisions.push({ decision: childDecision, direction });
						}
					}
					
					// Actors
					if (direction.responsibleUser) {
						actors.push({
							id: `user-${direction.responsibleUser.id}-${decision.id}-${idx}`,
							name: direction.responsibleUser.name || `${direction.responsibleUser.firstName} ${direction.responsibleUser.lastName}`.trim(),
							role: "User",
							direction
						});
					}
					if (direction.responsibleUnit) {
						actors.push({
							id: `unit-${direction.responsibleUnit.id}-${decision.id}-${idx}`,
							name: direction.responsibleUnit.name,
							role: "Unit",
							direction
						});
					}
				});
			}
			
			// Create nodes; dagre will handle placement
			if (childDecisions.length > 0) {
				childDecisions.forEach(({ decision: childDecision, direction }, idx) => {
					const childId = `decision-${childDecision.id}`;
					
					// Check if this child has its own children
					const childDirections = allDecisions.find(d => d.id === childDecision.id)?.directions || [];
					const hasGrandChildren = childDirections.some(d => d.followDecision || d.responsibleUser || d.responsibleUnit);
					
					addNodeUnique({
						id: childId,
						type: "decision",
						position: { x: 0, y: 0 },
						data: { 
							title: childDecision.name, 
							unit: childDecision.unit?.name || "N/A", 
							decisionId: childDecision.id, 
							isCurrentDecision: false,
							hasChildren: hasGrandChildren,
							isExpanded: expandedDecisions.has(childDecision.id),
							onToggleExpand: toggleDecisionExpansion
						}
					});
					
					// Connect to parent with animated labels
					addEdgeUnique({
						id: `edge-${decision.id}-to-${childId}`,
						source: `decision-${decision.id}`,
						target: childId,
						type: "animatedLabel",
						animated: true,
						markerEnd: { type: MarkerType.ArrowClosed, color: "#0ea5e9", width: 16, height: 16 },
						style: { 
							stroke: "#0ea5e9", 
							strokeWidth: 2
						},
						data: {
							label: direction?.description || "Sub-decision",
							tier: "decision",
							edgeType: "hierarchy",
							color: "#0369a1"
						},
					});
					
					// Recursively process this child's children if expanded
					if (expandedDecisions.has(childDecision.id)) {
						const childDirectionsData = allDecisions.find(d => d.id === childDecision.id)?.directions || [];
						processDecisionLevel(childDecision, childDirectionsData, level + 1, 0, true, 0);
					}
				});
			}
			
			// Actors
			if (actors.length > 0) {
				actors.forEach((actor, idx) => {
					addNodeUnique({
						id: actor.id,
						type: "actor",
						position: { x: 0, y: 0 },
						data: { name: actor.name, role: actor.role }
					});
					
					// Actor connections with smooth animations
					addEdgeUnique({
						id: `edge-${decision.id}-to-${actor.id}`,
						source: `decision-${decision.id}`,
						target: actor.id,
						type: "animatedLabel",
						animated: false,
						markerEnd: { type: MarkerType.ArrowClosed, color: "#8b5cf6", width: 16, height: 16 },
						style: { 
							stroke: "#8b5cf6", 
							strokeWidth: 2
						},
						data: {
							label: actor.direction?.description || "Responsible",
							tier: "actor",
							edgeType: "involvement",
							color: "#7c3aed"
						},
					});
				});
			}
			
			// Receivers
			if (receivers.length > 0) {
				receivers.forEach((receiver, idx) => {
					const receiverId = `receiver-${decision.id}-${idx}`;
					
					addNodeUnique({
						id: receiverId,
						type: "receiver",
						position: { x: 0, y: 0 },
						data: { 
							receiverName: receiver.name || receiver.displayName || "Decision Receiver"
						}
					});
					
					// Receiver connections with impact styling
					addEdgeUnique({
						id: `edge-${decision.id}-to-${receiverId}`,
						source: `decision-${decision.id}`,
						target: receiverId,
						type: "animatedLabel",
						animated: false,
						markerEnd: { type: MarkerType.ArrowClosed, color: "#10b981", width: 16, height: 16 },
						style: { 
							stroke: "#10b981", 
							strokeWidth: 2
						},
						data: {
							label: receiver.direction?.description || "Affects",
							tier: "receiver",
							edgeType: "impact",
							color: "#059669"
						},
					});
				});
			}
			
			return 0; // No longer tracking currentIndex
		};
		// Start processing from root level
		if (expandedDecisions.has(decisionData.id)) {
			processDecisionLevel(decisionData, directionsData, 1, 0, true, 0);
		}

		// REMOVED: Parent decisions display
		// We no longer show parent decisions in the tree to keep focus on current decision and its children
		// This improves clarity by showing only the current decision and its children (sub-decisions, actors, receivers)

		return layoutGraph(nodes, edges);
	}, [decisionData, directionsData, allDecisions, decisionReceivers, expandedDecisions, layoutGraph]);

	// Sync React Flow state and fit view when data changes
	useEffect(() => { setNodes(buildDecisionTree.nodes); }, [buildDecisionTree.nodes, setNodes]);
	useEffect(() => { setEdges(buildDecisionTree.edges); }, [buildDecisionTree.edges, setEdges]);
	
	// Only center view on initial load, not on expansions
	useEffect(() => {
		if (rfInstance && buildDecisionTree.nodes.length > 0) {
			// Only fit view once when component first mounts
			const timeoutId = setTimeout(() => {
				rfInstance.fitView({ 
					padding: 0.15, 
					includeHiddenNodes: true, 
					duration: 1200,
					minZoom: 0.15,
					maxZoom: 0.6 
				});
			}, 400);
			
			return () => clearTimeout(timeoutId);
		}
	}, [rfInstance]); // Remove dependency on expandedDecisions to prevent jumping

	const onConnect = useCallback((params) => setEdges((eds) => addEdge(params, eds)), [setEdges]);

	return (
		<div style={{ width: "100%", height: "100%" }}>
			<ReactFlow
				nodes={nodesState}
				edges={edgesState}
				onNodesChange={onNodesChange}
				onEdgesChange={onEdgesChange}
				onConnect={onConnect}
				nodeTypes={nodeTypes}
				edgeTypes={edgeTypes}
				fitView={false}
				defaultViewport={{ x: -800, y: -150, zoom: 0.3 }}
				snapToGrid
				snapGrid={[25, 25]}
				minZoom={0.2}
				maxZoom={1.5}
				panOnDrag
				zoomOnDoubleClick={false}
				nodesDraggable={false}
				nodesConnectable={false}
				elementsSelectable={true}
				defaultEdgeOptions={{ 
					type: "animatedLabel", 
					markerEnd: { type: MarkerType.ArrowClosed, color: "#94a3b8", width: 16, height: 16 },
					style: { strokeWidth: 2 }
				}}
				onInit={(instance) => {
					setRfInstance(instance);
					// Only center view once on initial load
					setTimeout(() => {
						try { 
							instance.fitView({ 
								padding: 0.3, 
								includeHiddenNodes: true, 
								duration: 600,
								minZoom: 0.4,
								maxZoom: 1.0 
							}); 
						} catch (e) { 
							console.debug("Initial fitView completed"); 
						}
					}, 200);
				}}
				attributionPosition="bottom-left"
				proOptions={{ hideAttribution: true }}
			>
				<Controls 
					position="top-left" 
					showInteractive={false}
					style={{
						background: "rgba(248, 250, 252, 0.95)",
						border: "1px solid #e2e8f0",
						borderRadius: "12px",
						boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
						backdropFilter: "blur(10px)"
					}}
				/>
				<MiniMap 
					nodeColor={(node) => {
						if (node?.type === "decision") {
							return node?.data?.isCurrentDecision ? "#0ea5e9" : "#94a3b8";
						}
						if (node?.type === "actor") {
							return "#8b5cf6";
						}
						if (node?.type === "receiver") {
							return "#10b981";
						}
						return "#f1f5f9";
					}}
					nodeStrokeColor={(node) => {
						if (node?.type === "decision") {
							return node?.data?.isCurrentDecision ? "#0284c7" : "#64748b";
						}
						if (node?.type === "actor") {
							return "#7c3aed";
						}
						if (node?.type === "receiver") {
							return "#059669";
						}
						return "#cbd5e1";
					}}
					nodeBorderRadius={12}
					position="bottom-right"
					style={{ 
						backgroundColor: "rgba(248, 250, 252, 0.95)", 
						border: "1px solid #e2e8f0",
						borderRadius: "12px",
						boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
						backdropFilter: "blur(10px)"
					}}
				/>
				<Background 
					color="#e2e8f0" 
					gap={20} 
					size={1.5}
					style={{ backgroundColor: "#f8fafc" }}
				/>
			</ReactFlow>
		</div>
	);
};

// PropTypes
AnimatedLabelEdge.propTypes = {
	id: PropTypes.string.isRequired,
	sourceX: PropTypes.number.isRequired,
	sourceY: PropTypes.number.isRequired,
	targetX: PropTypes.number.isRequired,
	targetY: PropTypes.number.isRequired,
	sourcePosition: PropTypes.string.isRequired,
	targetPosition: PropTypes.string.isRequired,
	data: PropTypes.shape({
		label: PropTypes.string,
		tier: PropTypes.string,
		edgeType: PropTypes.string,
		animated: PropTypes.bool,
		color: PropTypes.string,
	}),
	markerEnd: PropTypes.string,
	style: PropTypes.object,
};

DecisionNode.propTypes = {
	data: PropTypes.shape({
		title: PropTypes.string.isRequired,
		unit: PropTypes.string.isRequired,
		decisionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		isCurrentDecision: PropTypes.bool,
		isSubDecision: PropTypes.bool,
		hasChildren: PropTypes.bool,
		isExpanded: PropTypes.bool,
		onToggleExpand: PropTypes.func
	}).isRequired,
	selected: PropTypes.bool,
};

ActorNode.propTypes = {
	data: PropTypes.shape({
		name: PropTypes.string.isRequired,
		role: PropTypes.string.isRequired
	}).isRequired,
	selected: PropTypes.bool,
};

ReceiverNode.propTypes = {
	data: PropTypes.shape({
		receiverName: PropTypes.string.isRequired
	}).isRequired,
	selected: PropTypes.bool,
};

DecisionTree.propTypes = {
	decisionData: PropTypes.object.isRequired,
	directionsData: PropTypes.array,
	allDecisions: PropTypes.array,
	incomingRelations: PropTypes.array,
	decisionReceivers: PropTypes.array
};

export default DecisionTree;
